import { HttpClient, HttpParams, httpResource } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { ApiListResponse, ApiResponse } from '@mb/common';
import { firstValueFromAbortable } from '@mb/common/client';
import { firstValueFrom } from 'rxjs';
import {
  CreatePersonInput,
  IPerson,
  ListPersonsInput,
  UpdatePersonInput,
} from '../../shared';

@Injectable({ providedIn: 'root' })
export class ContactsApiService {
  readonly #http = inject(HttpClient);

  readonly #personsBase = '/api/v1/persons';
  readonly #organizationsBase = '/api/v1/organization/organizations';

  createPerson(input: CreatePersonInput) {
    return firstValueFrom(
      this.#http.post<ApiResponse<IPerson>>(this.#personsBase, input),
    );
  }

  getPerson(id: string, options?: { signal?: AbortSignal }) {
    return firstValueFromAbortable(
      this.#http.get<ApiResponse<IPerson>>(`${this.#personsBase}/${id}`),
      options?.signal,
    );
  }

  personResource(id: () => string | undefined) {
    return httpResource(
      () => {
        const value = id();
        return value
          ? {
              url: `${this.#personsBase}/${value}`,
              method: 'GET' as const,
              withCredentials: true,
            }
          : undefined;
      },
      {
        parse: (raw: unknown): IPerson | null =>
          (raw as ApiResponse<IPerson>).data ?? null,
      },
    );
  }

  listPersons(input: ListPersonsInput, options?: { signal?: AbortSignal }) {
    return firstValueFromAbortable(
      this.#http.get<ApiListResponse<IPerson>>(this.#personsBase, {
        params: this.#listParams(input),
      }),
      options?.signal,
    );
  }

  updatePerson(input: UpdatePersonInput) {
    return firstValueFrom(
      this.#http.patch<ApiResponse<IPerson>>(
        `${this.#personsBase}/${input.id}`,
        input,
      ),
    );
  }

  deletePerson(id: string) {
    return firstValueFrom(
      this.#http.delete<ApiResponse<IPerson>>(`${this.#personsBase}/${id}`),
    );
  }

  resendPersonInvite(organizationId: string, personId: string) {
    return firstValueFrom(
      this.#http.post<{ success: boolean; data: { inviteUrl: string | null } }>(
        `${this.#organizationsBase}/${organizationId}/persons/${personId}/resend-invite`,
        {},
      ),
    );
  }

  unlinkPersonFromUser(personId: string) {
    return firstValueFrom(
      this.#http.post<ApiResponse<IPerson>>(
        `${this.#personsBase}/${personId}/unlink-user`,
        {},
      ),
    );
  }

  #listParams(input: ListPersonsInput): HttpParams {
    let params = new HttpParams()
      .set('limit', String(input.limit ?? 10))
      .set('sortBy', input.sortBy ?? 'createdAt')
      .set('sortOrder', input.sortOrder ?? 'desc');

    if (input.organizationId) {
      params = params.set('organizationId', input.organizationId);
    }
    if (input.search) {
      params = params.set('search', input.search);
    }
    if (input.organizationUnitId) {
      params = params.set('organizationUnitId', input.organizationUnitId);
    }
    if (input.cursor) {
      params = params.set('cursor', JSON.stringify(input.cursor));
    }
    if (input.filters) {
      params = params.set('filters', JSON.stringify(input.filters));
    }
    return params;
  }
}
